// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { ShopSettingsClass, ShopProductClass, ProductRewardClass } from '../../classes/ShopSettings';
import { ProductType, RewardType, getProductTypeName, getRewardTypeName } from '../../types/shopConfig';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import UnsavedChangesModal from '../UnsavedChangesModal';

interface ShopConfigurationProps {
  onError: (error: string) => void;
}

interface ProductCardProps {
  productKey: string;
  product: ShopProductClass;
  expandedProducts: Set<string>;
  hasEditPermission: boolean;
  onToggleExpansion: (key: string) => void;
  onProductChange: (productKey: string, field: string, value: any) => void;
  onRewardChange: (productKey: string, rewardIndex: number, field: string, value: any) => void;
  onAddReward: (productKey: string) => void;
  onRemoveReward: (productKey: string, rewardIndex: number) => void;
  onRemoveProduct: (productKey: string) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  productKey,
  product,
  expandedProducts,
  hasEditPermission,
  onToggleExpansion,
  onProductChange,
  onRewardChange,
  onAddReward,
  onRemoveReward,
  onRemoveProduct,
}) => {
  return (
    <div className={`product-card ${product.enabled ? 'enabled' : 'disabled'}`}>
      <div className="product-header" onClick={() => onToggleExpansion(productKey)}>
        <div className="product-info">
          <span className="expand-icon">
            {expandedProducts.has(productKey) ? '▼' : '▶'}
          </span>
          <div className="product-title">
            <h4>{product.productID}</h4>
            <div className="product-badges">
              <span className={`type-badge ${product.type}`}>
                {getProductTypeName(product.type)}
              </span>
              <span className={`status-badge ${product.enabled ? 'enabled' : 'disabled'}`}>
                {product.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <span className="priority-badge">Priority: {product.priority}</span>
            </div>
          </div>
        </div>
        <div className="product-summary">
          <div className="coin-value">
            {product.getTotalCoinValue()} coins
          </div>
          {hasEditPermission && (
            <button 
              className="btn btn-danger btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveProduct(productKey);
              }}
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      {expandedProducts.has(productKey) && (
        <div className="product-details">
          <div className="form-grid">
            <div className="form-group">
              <label>Product ID:</label>
              <input
                type="text"
                value={product.productID}
                onChange={(e) => onProductChange(productKey, 'productID', e.target.value)}
                className="form-control"
                disabled={!hasEditPermission}
              />
            </div>

            <div className="form-group">
              <label>iOS ID:</label>
              <input
                type="text"
                value={product.iosID}
                onChange={(e) => onProductChange(productKey, 'iosID', e.target.value)}
                className="form-control"
                disabled={!hasEditPermission}
              />
            </div>

            <div className="form-group">
              <label>Google ID:</label>
              <input
                type="text"
                value={product.googleID}
                onChange={(e) => onProductChange(productKey, 'googleID', e.target.value)}
                className="form-control"
                disabled={!hasEditPermission}
              />
            </div>

            <div className="form-group">
              <label>Priority:</label>
              <input
                type="number"
                value={product.priority}
                onChange={(e) => onProductChange(productKey, 'priority', parseInt(e.target.value))}
                className="form-control"
                min="0"
                disabled={!hasEditPermission}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={product.enabled}
                  onChange={(e) => onProductChange(productKey, 'enabled', e.target.checked)}
                  disabled={!hasEditPermission}
                />
                Enabled
              </label>
            </div>
          </div>

          {/* Rewards Section */}
          <div className="rewards-section">
            <div className="rewards-header">
              <h5>Rewards</h5>
              {hasEditPermission && (
                <button 
                  className="btn btn-success btn-sm"
                  onClick={() => onAddReward(productKey)}
                >
                  ➕ Add Reward
                </button>
              )}
            </div>

            <div className="rewards-list">
              {product.rewards.map((reward, index) => (
                <div key={index} className="reward-item">
                  <div className="reward-fields">
                    <div className="form-group">
                      <label>Type:</label>
                      <select
                        value={reward.type}
                        onChange={(e) => onRewardChange(productKey, index, 'type', e.target.value)}
                        className="form-control"
                        disabled={!hasEditPermission}
                      >
                        {Object.values(RewardType).filter(v => typeof v === 'number').map(type => (
                          <option key={type} value={type}>{getRewardTypeName(type as RewardType)}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>Amount:</label>
                      <input
                        type="number"
                        value={reward.amount}
                        onChange={(e) => onRewardChange(productKey, index, 'amount', e.target.value)}
                        className="form-control"
                        min="1"
                        disabled={!hasEditPermission}
                      />
                    </div>
                  </div>

                  {hasEditPermission && product.rewards.length > 1 && (
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => onRemoveReward(productKey, index)}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ShopConfiguration: React.FC<ShopConfigurationProps> = ({ onError }) => {
  const [shopSettings, setShopSettings] = useState<ShopSettingsClass | null>(null);
  const [originalSettings, setOriginalSettings] = useState<ShopSettingsClass | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // New product form
  const [newProduct, setNewProduct] = useState<ShopProductClass>(
    new ShopProductClass('', ProductType.Consumable, '', '', true, 0, [new ProductRewardClass(RewardType.Coin, 100)])
  );

  const hasEditPermission = true;
  const hasUnsavedChanges = originalSettings ? shopSettings?.hasChangedFrom(originalSettings) || false : false;

  // Auto-hide success/error messages after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Fetch shop configuration
  const fetchShopConfig = async () => {
    try {
      setLoading(true);
      const configData = await consoleService.getShopConfig();
      console.log('[ShopConfiguration] Raw response:', configData);
      console.log('[ShopConfiguration] Response body:', configData?.body);
      console.log('[ShopConfiguration] Body type:', typeof configData?.body);
      
      // Handle different response formats from Nakama RPC
      let parsedConfig;
      if (configData?.body && typeof configData.body === 'string' && configData.body.trim()) {
        // Response has body as string - parse it (standard Nakama RPC response)
        parsedConfig = JSON.parse(configData.body);
      } else if (configData?.body && typeof configData.body === 'object') {
        // Body is already parsed object
        parsedConfig = configData.body;
      } else if (configData?.products) {
        // Response is already the config object
        parsedConfig = configData;
      } else if (typeof configData === 'string' && configData.trim()) {
        // Response is a string directly
        parsedConfig = JSON.parse(configData);
      } else {
        // Empty or invalid response - use default settings
        console.warn('[ShopConfiguration] Empty response from backend, using default settings');
        const defaultSettings = new ShopSettingsClass();
        setShopSettings(defaultSettings);
        setOriginalSettings(defaultSettings.clone());
        return;
      }
      
      console.log('[ShopConfiguration] Parsed config:', parsedConfig);
      console.log('[ShopConfiguration] Products count:', Object.keys(parsedConfig?.products || {}).length);
      
      const settings = ShopSettingsClass.fromObject(parsedConfig);
      setShopSettings(settings);
      setOriginalSettings(settings.clone());
    } catch (err) {
      console.error('[ShopConfiguration] Error fetching config:', err);
      onError(err instanceof Error ? err.message : 'Failed to fetch shop configuration');
    } finally {
      setLoading(false);
    }
  };

  // Save shop configuration using shop_config_resetup RPC
  const saveShopConfig = async () => {
    if (!shopSettings) return;

    try {
      setSaving(true);
      setErrorMessage(null);
      const configData = shopSettings.toObject();
      
      // Use publishShopConfig which calls shop_config_resetup RPC
      await consoleService.publishShopConfig(configData);
      
      setOriginalSettings(shopSettings.clone());
      setSuccessMessage('✅ Shop configuration saved successfully!');
      console.log('[ShopConfiguration] Configuration saved successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save shop configuration';
      setErrorMessage(`❌ ${errorMsg}`);
      onError(errorMsg);
      console.error('[ShopConfiguration] Failed to save:', err);
      throw err; // Re-throw for unsaved changes handling
    } finally {
      setSaving(false);
    }
  };

  const {
    showUnsavedChangesModal,
    isSaving,
    confirmLeave,
    saveAndLeave,
    cancelLeave
  } = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: saveShopConfig,
    message: 'You have unsaved changes to the shop configuration. Do you want to save them before leaving?'
  });

  useEffect(() => {
    fetchShopConfig();
  }, []);

  // Handle product field changes
  const handleProductChange = (productKey: string, field: string, value: any) => {
    if (!shopSettings) return;

    const updatedSettings = shopSettings.clone();
    updatedSettings.updateProductField(productKey, field, value);
    setShopSettings(updatedSettings);
  };

  // Handle reward changes
  const handleRewardChange = (productKey: string, rewardIndex: number, field: string, value: any) => {
    if (!shopSettings) return;

    const updatedSettings = shopSettings.clone();
    const product = updatedSettings.getProduct(productKey);
    if (product && product.rewards[rewardIndex]) {
      (product.rewards[rewardIndex] as any)[field] = field === 'type' ? parseInt(value) : 
                                                     field === 'amount' ? parseInt(value) : value;
    }
    setShopSettings(updatedSettings);
  };

  // Add new reward to product
  const addReward = (productKey: string) => {
    if (!shopSettings) return;

    const updatedSettings = shopSettings.clone();
    const product = updatedSettings.getProduct(productKey);
    if (product) {
      product.addReward(new ProductRewardClass(RewardType.Coin, 100));
    }
    setShopSettings(updatedSettings);
  };

  // Remove reward from product
  const removeReward = (productKey: string, rewardIndex: number) => {
    if (!shopSettings) return;

    const updatedSettings = shopSettings.clone();
    const product = updatedSettings.getProduct(productKey);
    if (product) {
      product.removeReward(rewardIndex);
    }
    setShopSettings(updatedSettings);
  };

  // Add new product
  const handleAddProduct = () => {
    if (!shopSettings || !newProduct.productID) return;

    const updatedSettings = shopSettings.clone();
    updatedSettings.addProduct(newProduct.productID, newProduct.clone());
    setShopSettings(updatedSettings);
    
    // Reset form
    setNewProduct(new ShopProductClass('', ProductType.Consumable, '', '', true, 0, [new ProductRewardClass(RewardType.Coin, 100)]));
    setShowProductModal(false);
  };

  // Remove product
  const removeProduct = (productKey: string) => {
    if (!shopSettings) return;

    const updatedSettings = shopSettings.clone();
    updatedSettings.removeProduct(productKey);
    setShopSettings(updatedSettings);
  };

  // Toggle product expansion
  const toggleProductExpansion = (productKey: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productKey)) {
      newExpanded.delete(productKey);
    } else {
      newExpanded.add(productKey);
    }
    setExpandedProducts(newExpanded);
  };

  if (loading) {
    return (
      <div className="shop-configuration">
        <div className="loading-state">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading shop configuration...</span>
          </div>
          <p>Loading shop configuration...</p>
        </div>
      </div>
    );
  }

  if (!shopSettings) {
    return (
      <div className="shop-configuration">
        <div className="alert alert-info">
          No shop configuration available.
        </div>
      </div>
    );
  }

  const sortedProducts = shopSettings.getProductsSortedByPriority();

  return (
    <div className="shop-configuration">
      {/* Header */}
      <div className="section-header">
        <div className="header-content">
          <h3>🛒 Shop Configuration</h3>
        </div>
        <div className="header-actions">
          {hasEditPermission && (
            <>
              <button 
                className="btn btn-success"
                onClick={() => setShowProductModal(true)}
              >
                ➕ Add Product
              </button>
              <button 
                className="btn btn-primary"
                onClick={saveShopConfig}
                disabled={saving || !hasUnsavedChanges}
              >
                {saving ? '💾 Saving...' : '💾 Save Configuration'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toast Messages */}
      {successMessage && (
        <div className="toast toast-success" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          backgroundColor: '#28a745',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          animation: 'slideIn 0.3s ease'
        }}>
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="toast toast-error" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          backgroundColor: '#dc3545',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          animation: 'slideIn 0.3s ease'
        }}>
          {errorMessage}
        </div>
      )}

      {/* Products List - Drag and drop temporarily disabled */}
      <div className="products-list">
        {sortedProducts.map(({ key, product }) => (
          <ProductCard
            key={key}
            productKey={key}
            product={product}
            expandedProducts={expandedProducts}
            hasEditPermission={hasEditPermission}
            onToggleExpansion={toggleProductExpansion}
            onProductChange={handleProductChange}
            onRewardChange={handleRewardChange}
            onAddReward={addReward}
            onRemoveReward={removeReward}
            onRemoveProduct={removeProduct}
          />
        ))}
      </div>

      {/* Add Product Modal */}
      {showProductModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Product</h3>
              <button className="modal-close" onClick={() => setShowProductModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Product ID:</label>
                <input
                  type="text"
                  value={newProduct.productID}
                  onChange={(e) => setNewProduct(new ShopProductClass(
                    e.target.value, newProduct.type, newProduct.iosID, newProduct.googleID,
                    newProduct.enabled, newProduct.priority, newProduct.rewards, newProduct.json_config
                  ))}
                  className="form-control"
                  placeholder="e.g., coins_100"
                />
              </div>

              <div className="form-group">
                <label>iOS ID:</label>
                <input
                  type="text"
                  value={newProduct.iosID}
                  onChange={(e) => setNewProduct(new ShopProductClass(
                    newProduct.productID, newProduct.type, e.target.value, newProduct.googleID,
                    newProduct.enabled, newProduct.priority, newProduct.rewards, newProduct.json_config
                  ))}
                  className="form-control"
                  placeholder="com.investaplay.investaplay.coins_100"
                />
              </div>

              <div className="form-group">
                <label>Google ID:</label>
                <input
                  type="text"
                  value={newProduct.googleID}
                  onChange={(e) => setNewProduct(new ShopProductClass(
                    newProduct.productID, newProduct.type, newProduct.iosID, e.target.value,
                    newProduct.enabled, newProduct.priority, newProduct.rewards, newProduct.json_config
                  ))}
                  className="form-control"
                  placeholder="com.investaplay.games.coins1"
                />
              </div>

              <div className="form-group">
                <label>Priority:</label>
                <input
                  type="number"
                  value={newProduct.priority}
                  onChange={(e) => setNewProduct(new ShopProductClass(
                    newProduct.productID, newProduct.type, newProduct.iosID, newProduct.googleID,
                    newProduct.enabled, parseInt(e.target.value), newProduct.rewards, newProduct.json_config
                  ))}
                  className="form-control"
                  min="0"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProductModal(false)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleAddProduct}
                disabled={!newProduct.productID}
              >
                Add Product
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        message="You have unsaved changes to the shop configuration. Do you want to save them before leaving?"
        onSaveAndLeave={saveAndLeave}
        onLeaveWithoutSaving={confirmLeave}
        onCancel={cancelLeave}
        isSaving={isSaving}
        showSaveOption={hasEditPermission}
      />
    </div>
  );
};

export default ShopConfiguration;
